import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  api,
  BladApi,
  type DefinicjaAtrybutu,
  type Karta as KartaUrzadzenia,
  type Sesja,
  type ZmianaKarty,
} from '../api';
import { data, doPolaDaty, takNie } from '../format';
import { useIdOperacji } from '../operacje';
import { adresSkanu, idz } from '../router';
import { IkonaDrukuj, IkonaWstecz } from '../components/Icons';
import { Qr } from '../components/Qr';
import { NaglowekWidoku } from '../components/Shell';
import { GaleriaZalacznikow } from '../components/Zdjecia';
import {
  ChipStatusu,
  Dana,
  Karta,
  Komunikat,
  PoleTekstowe,
  Przycisk,
  PrzyciskMarki,
  Tag,
} from '../components/ui';

/** Pola podzespołów — te same nazwy, co na druku montażu. */
const PODZESPOLY = [
  { kod: 'modelGlowicy', etykieta: 'Model głowicy' },
  { kod: 'modelNapedu', etykieta: 'Model napędu' },
  { kod: 'podstawa', etykieta: 'Podstawa' },
  { kod: 'blat', etykieta: 'Blat' },
] as const;

const DATY = [
  { kod: 'sealDate', etykieta: 'Data z plomby' },
  { kod: 'dataZakupu', etykieta: 'Data zakupu' },
  { kod: 'dataInstalacji', etykieta: 'Data instalacji' },
  { kod: 'gwarancjaDo', etykieta: 'Gwarancja do' },
  { kod: 'nastepnyPrzeglad', etykieta: 'Następny przegląd' },
] as const;

const ZRODLA: Record<string, string> = {
  MONTAZ: 'montaż',
  SERWIS: 'przyjęcie na serwis',
  IMPORT_SUBIEKT: 'import z Subiekta',
  RECZNIE: 'wpis ręczny',
};

/**
 * Karta urządzenia — pełna historia maszyny, rdzeń produktu (CLAUDE.md §0).
 * Układ dwukolumnowy z prototypu: po lewej dane, po prawej to, po co sięga się wzrokiem.
 *
 * Nie ma tu listy „zmień status": status jest skutkiem ubocznym pracy serwisowej
 * i zacznie się zmieniać razem ze zleceniami w Etapie 4 (zasada 3).
 */
export function DeviceView({ id, sesja }: { id: string; sesja: Sesja }) {
  const mozeEdytowac = sesja.permissions.includes('device.write');

  const [karta, setKarta] = useState<KartaUrzadzenia | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [edycja, setEdycja] = useState(false);

  useEffect(() => {
    let aktualne = true;
    setKarta(null);
    setEdycja(false);

    api
      .urzadzenie(id)
      .then((wynik) => aktualne && setKarta(wynik))
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać karty.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [id]);

  if (blad) {
    return (
      <>
        <NaglowekWidoku tytul="Karta urządzenia" />
        <Komunikat tresc={blad} />
        <div className="mt-14">
          <Przycisk type="button" onClick={() => idz('/urzadzenia')} ikona={<IkonaWstecz rozmiar={16} />}>
            Wróć do listy
          </Przycisk>
        </div>
      </>
    );
  }

  if (!karta) {
    return (
      <>
        <NaglowekWidoku tytul="Karta urządzenia" />
        <p className="t-small text-n-500">Wczytywanie…</p>
      </>
    );
  }

  const { urzadzenie } = karta;

  return (
    <>
      <NaglowekWidoku
        tytul={`${urzadzenie.producent} ${urzadzenie.model}`}
        podtytul={`Karta urządzenia · nr boczny ${urzadzenie.sideNumber} · ${urzadzenie.customer.nazwa}`}
        akcje={
          <>
            <Przycisk
              type="button"
              onClick={() => idz('/urzadzenia')}
              ikona={<IkonaWstecz rozmiar={16} />}
            >
              Lista
            </Przycisk>
            {mozeEdytowac && !edycja && (
              <PrzyciskMarki type="button" onClick={() => setEdycja(true)}>
                Uzupełnij kartę
              </PrzyciskMarki>
            )}
          </>
        }
      />

      {createPortal(<Etykieta karta={karta} />, document.body)}

      <div className="grid gap-14 xl:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          {edycja ? (
            <FormularzKarty
              karta={karta}
              onZapisano={(nowa) => {
                setKarta(nowa);
                setEdycja(false);
              }}
              onAnuluj={() => setEdycja(false)}
            />
          ) : (
            <Podglad karta={karta} sesja={sesja} />
          )}
        </div>

        <div className="min-w-0">
          <Karta>
            <div className="text-center">
              <div className="mb-10 inline-block border border-n-100 p-9">
                <Qr tresc={adresSkanu(urzadzenie.qrCode)} rozmiar={132} />
              </div>
              <div className="t-mono-data text-n-900">{urzadzenie.qrCode}</div>
              <div className="t-small mb-13 text-n-600">nr boczny {urzadzenie.sideNumber}</div>
              <div className="flex justify-center">
                <Przycisk
                  type="button"
                  onClick={() => window.print()}
                  ikona={<IkonaDrukuj rozmiar={16} />}
                >
                  Drukuj etykietę
                </Przycisk>
              </div>
            </div>
          </Karta>

          <Karta tytul="Z Waszego druku">
            <dl className="kv">
              <dt>Numer boczny</dt>
              <dd className="t-mono-data">{urzadzenie.sideNumber}</dd>
              <dt>Numer fabryczny</dt>
              <dd className="t-mono-data">{urzadzenie.serialNumber}</dd>
              <dt>Data z plomby</dt>
              <dd>{data(urzadzenie.sealDate) || '—'}</dd>
              <dt>Gwarancja do</dt>
              <dd>{data(urzadzenie.gwarancjaDo) || '—'}</dd>
              <dt>Status</dt>
              <dd>
                <ChipStatusu status={urzadzenie.status} />
              </dd>
            </dl>
          </Karta>

          <Karta tytul={`Do uzupełnienia · ${karta.braki.length}`}>
            {karta.braki.length === 0 ? (
              <p className="t-small text-n-600">
                Karta ma wszystko, czego oczekuje kategoria „{karta.kategoria.nazwa}".
              </p>
            ) : (
              <>
                <ul className="m-0 list-none p-0">
                  {karta.braki.map((brak) => (
                    <li key={brak.kod} className="t-body border-b border-n-100 py-7 last:border-b-0">
                      {brak.etykieta}
                    </li>
                  ))}
                </ul>
                <p className="t-small mt-10 text-n-500">
                  To podpowiedź, nie wymóg — kartę uzupełnia się przez lata.
                </p>
              </>
            )}
          </Karta>
        </div>
      </div>
    </>
  );
}

/** Naklejka na maszynę. Widoczna wyłącznie na wydruku (patrz styles/index.css). */
function Etykieta({ karta }: { karta: KartaUrzadzenia }) {
  const { urzadzenie } = karta;
  return (
    <div className="etykieta">
      <div className="flex items-center gap-14">
        <Qr tresc={adresSkanu(urzadzenie.qrCode)} rozmiar={150} />
        <div>
          <div className="t-mono-data">{urzadzenie.qrCode}</div>
          <div className="t-h1">{urzadzenie.sideNumber}</div>
          <div className="t-body">
            {urzadzenie.producent} {urzadzenie.model}
          </div>
          <div className="t-small">{urzadzenie.customer.nazwa}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Podglad({ karta, sesja }: { karta: KartaUrzadzenia; sesja: Sesja }) {
  const { urzadzenie, atrybuty } = karta;
  const wartosci = urzadzenie.atrybuty;

  return (
    <>
      <Karta
        tytul="Wymagane — cztery pola"
        akcje={<Tag>{karta.sciezkaKategorii.map((k) => k.nazwa).join(' › ')}</Tag>}
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
          <Dana etykieta="Klient">{urzadzenie.customer.nazwa}</Dana>
          <Dana etykieta="Numer fabryczny" mono>
            {urzadzenie.serialNumber}
          </Dana>
          <Dana etykieta="Producent i model">
            {urzadzenie.producent} {urzadzenie.model}
          </Dana>
          <Dana etykieta="Kategoria">{karta.kategoria.nazwa}</Dana>
        </dl>
      </Karta>

      <Karta
        tytul="Szczegóły techniczne"
        akcje={<span className="t-small text-n-500">uzupełniane latami · nic nie jest wymagane</span>}
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
          {PODZESPOLY.map((pole) => (
            <Dana key={pole.kod} etykieta={pole.etykieta}>
              {(urzadzenie[pole.kod] as string | null) ?? ''}
            </Dana>
          ))}
          <Dana etykieta="Nowa / używana">
            {urzadzenie.origin === 'NOWA' ? 'nowa' : urzadzenie.origin === 'UZYWANA' ? 'używana' : ''}
          </Dana>
          <Dana etykieta="Rok produkcji" mono>
            {urzadzenie.rokProdukcji === null ? '' : String(urzadzenie.rokProdukcji)}
          </Dana>
          <Dana etykieta="Sprężone powietrze">{takNie(urzadzenie.sprezonePowietrze)}</Dana>
          <Dana etykieta="Licznik motogodzin" mono>
            {urzadzenie.licznikMotogodzin === null ? '' : String(urzadzenie.licznikMotogodzin)}
          </Dana>
          <Dana etykieta="Data zakupu">{data(urzadzenie.dataZakupu)}</Dana>
          <Dana etykieta="Data instalacji">{data(urzadzenie.dataInstalacji)}</Dana>
          <Dana etykieta="Następny przegląd">{data(urzadzenie.nastepnyPrzeglad)}</Dana>
          <Dana etykieta="Źródło karty">{ZRODLA[urzadzenie.source]}</Dana>
        </dl>

        {urzadzenie.source === 'MONTAZ' && (
          <p className="t-small mt-13 text-n-500">
            Podzespoły przepisane ze zlecenia montażu — nikt nie wpisywał ich drugi raz.
          </p>
        )}
      </Karta>

      {atrybuty.length > 0 && (
        <Karta tytul={`Atrybuty kategorii · ${karta.kategoria.nazwa}`}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
            {atrybuty.map((definicja) => (
              <Dana
                key={definicja.kod}
                etykieta={
                  definicja.jednostka
                    ? `${definicja.nazwa} (${definicja.jednostka})`
                    : definicja.nazwa
                }
              >
                {pokazAtrybut(definicja, wartosci[definicja.kod])}
              </Dana>
            ))}
          </dl>
        </Karta>
      )}

      <Karta tytul="Zdjęcia i dokumenty">
        <GaleriaZalacznikow
          ownerType="DEVICE"
          ownerId={karta.urzadzenie.id}
          rodzaje={['ZDJECIE_OGOLNE', 'TABLICZKA', 'PO_NAPRAWIE', 'DTR']}
          mozeDodawac={sesja.permissions.includes('attachment.write')}
          mozeKasowac={sesja.permissions.includes('attachment.delete')}
        />
      </Karta>

      <Karta tytul="Historia serwisowa">
        <p className="t-small text-n-600">
          Pierwszy protokół pojawi się tutaj automatycznie. Zlecenia i protokoły powstają
          w Etapie 4 — do tego czasu karta gromadzi dane techniczne, nie zdarzenia.
        </p>
      </Karta>
    </>
  );
}

function pokazAtrybut(definicja: DefinicjaAtrybutu, wartosc: unknown): string {
  if (wartosc === null || wartosc === undefined || wartosc === '') return '';
  if (definicja.typ === 'BOOL') return wartosc ? 'tak' : 'nie';
  if (definicja.typ === 'DATA') return data(String(wartosc));
  return String(wartosc);
}

// ---------------------------------------------------------------------------

type Formularz = Record<string, string>;

function doFormularza(karta: KartaUrzadzenia): Formularz {
  const { urzadzenie } = karta;
  const wartosci: Formularz = {
    serialNumber: urzadzenie.serialNumber,
    producent: urzadzenie.producent,
    model: urzadzenie.model,
    sideNumber: urzadzenie.sideNumber,
    origin: urzadzenie.origin ?? '',
    rokProdukcji: urzadzenie.rokProdukcji === null ? '' : String(urzadzenie.rokProdukcji),
    licznikMotogodzin:
      urzadzenie.licznikMotogodzin === null ? '' : String(urzadzenie.licznikMotogodzin),
    sprezonePowietrze:
      urzadzenie.sprezonePowietrze === null ? '' : String(urzadzenie.sprezonePowietrze),
  };

  for (const pole of PODZESPOLY) wartosci[pole.kod] = (urzadzenie[pole.kod] as string | null) ?? '';
  for (const pole of DATY) wartosci[pole.kod] = doPolaDaty(urzadzenie[pole.kod] as string | null);

  for (const definicja of karta.atrybuty) {
    const wartosc = urzadzenie.atrybuty[definicja.kod];
    wartosci[`atr:${definicja.kod}`] =
      wartosc === null || wartosc === undefined ? '' : String(wartosc);
  }

  return wartosci;
}

/**
 * Uzupełnianie karty. Wysyłamy wyłącznie to, co użytkownik zmienił — PATCH z całym
 * formularzem nadpisywałby pola, których nikt nie dotknął.
 */
function FormularzKarty({
  karta,
  onZapisano,
  onAnuluj,
}: {
  karta: KartaUrzadzenia;
  onZapisano: (karta: KartaUrzadzenia) => void;
  onAnuluj: () => void;
}) {
  const operacja = useIdOperacji();
  const poczatkowy = doFormularza(karta);
  const [wartosci, setWartosci] = useState<Formularz>(poczatkowy);
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  const ustaw = (kod: string, wartosc: string): void =>
    setWartosci((poprzednie) => ({ ...poprzednie, [kod]: wartosc }));

  const brakujace = new Set(karta.braki.map((brak) => brak.kod));

  async function zapisz(): Promise<void> {
    setZapis(true);
    setBlad(null);

    const zmiany: ZmianaKarty = {};
    const atrybuty: Record<string, unknown> = {};

    for (const [kod, wartosc] of Object.entries(wartosci)) {
      if (wartosc === poczatkowy[kod]) continue;

      if (kod.startsWith('atr:')) {
        const definicja = karta.atrybuty.find((a) => `atr:${a.kod}` === kod);
        if (!definicja) continue;
        atrybuty[definicja.kod] = wartosc === '' ? null : przygotujAtrybut(definicja, wartosc);
        continue;
      }

      if (kod === 'rokProdukcji' || kod === 'licznikMotogodzin') {
        (zmiany as Record<string, unknown>)[kod] = wartosc === '' ? null : Number(wartosc);
      } else if (kod === 'sprezonePowietrze') {
        zmiany.sprezonePowietrze = wartosc === '' ? null : wartosc === 'true';
      } else if (kod === 'origin') {
        if (wartosc !== '') zmiany.origin = wartosc as 'NOWA' | 'UZYWANA';
      } else {
        (zmiany as Record<string, unknown>)[kod] = wartosc === '' ? null : wartosc;
      }
    }

    if (Object.keys(atrybuty).length > 0) zmiany.atrybuty = atrybuty;

    if (Object.keys(zmiany).length === 0) {
      onAnuluj();
      return;
    }

    try {
      const nowa = await api.zmienUrzadzenie(karta.urzadzenie.id, {
        ...zmiany,
        clientOperationId: operacja.biezacy(),
      });
      operacja.zakoncz();
      onZapisano(nowa);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się zapisać.');
      setZapis(false);
    }
  }

  return (
    <form
      onSubmit={(zdarzenie) => {
        zdarzenie.preventDefault();
        void zapisz();
      }}
      noValidate
    >
      {/* W podglądzie ta sekcja nazywa się „Wymagane — cztery pola", bo pokazuje klienta
          i kategorię. W edycji ich nie ma (maszyna nie zmienia właściciela z tego ekranu),
          za to jest numer boczny — więc nazywanie tego „czterema polami" wprowadzałoby w błąd. */}
      <Karta tytul="Identyfikacja">
        <div className="grid gap-13 sm:grid-cols-2">
          <PoleTekstowe
            etykieta="Numer fabryczny"
            value={wartosci.serialNumber}
            onChange={(z) => ustaw('serialNumber', z.target.value)}
          />
          <PoleTekstowe
            etykieta="Numer boczny"
            value={wartosci.sideNumber}
            onChange={(z) => ustaw('sideNumber', z.target.value)}
          />
          <PoleTekstowe
            etykieta="Producent"
            value={wartosci.producent}
            onChange={(z) => ustaw('producent', z.target.value)}
          />
          <PoleTekstowe
            etykieta="Model"
            value={wartosci.model}
            onChange={(z) => ustaw('model', z.target.value)}
          />
        </div>
      </Karta>

      <Karta tytul="Szczegóły techniczne">
        <div className="grid gap-13 sm:grid-cols-2">
          {PODZESPOLY.map((pole) => (
            <PoleTekstowe
              key={pole.kod}
              etykieta={etykietaZBrakiem(pole.etykieta, brakujace.has(pole.kod))}
              value={wartosci[pole.kod] ?? ''}
              onChange={(z) => ustaw(pole.kod, z.target.value)}
            />
          ))}

          <label className="block">
            <span className="t-label mb-5 block text-n-700">Nowa / używana</span>
            <select
              value={wartosci.origin}
              onChange={(z) => ustaw('origin', z.target.value)}
              className="w-full border border-n-300 bg-surface px-13 text-n-900"
            >
              <option value="">—</option>
              <option value="NOWA">nowa</option>
              <option value="UZYWANA">używana</option>
            </select>
          </label>

          <PoleTekstowe
            type="number"
            min={1900}
            max={2100}
            etykieta={etykietaZBrakiem('Rok produkcji', brakujace.has('rokProdukcji'))}
            value={wartosci.rokProdukcji}
            onChange={(z) => ustaw('rokProdukcji', z.target.value)}
          />

          <label className="block">
            <span className="t-label mb-5 block text-n-700">
              {etykietaZBrakiem('Sprężone powietrze', brakujace.has('sprezonePowietrze'))}
            </span>
            <select
              value={wartosci.sprezonePowietrze}
              onChange={(z) => ustaw('sprezonePowietrze', z.target.value)}
              className="w-full border border-n-300 bg-surface px-13 text-n-900"
            >
              <option value="">—</option>
              <option value="true">tak</option>
              <option value="false">nie</option>
            </select>
          </label>

          <PoleTekstowe
            type="number"
            min={0}
            etykieta={etykietaZBrakiem('Licznik motogodzin', brakujace.has('licznikMotogodzin'))}
            value={wartosci.licznikMotogodzin}
            onChange={(z) => ustaw('licznikMotogodzin', z.target.value)}
          />

          {DATY.map((pole) => (
            <PoleTekstowe
              key={pole.kod}
              type="date"
              etykieta={etykietaZBrakiem(pole.etykieta, brakujace.has(pole.kod))}
              value={wartosci[pole.kod] ?? ''}
              onChange={(z) => ustaw(pole.kod, z.target.value)}
            />
          ))}
        </div>
      </Karta>

      {karta.atrybuty.length > 0 && (
        <Karta tytul={`Atrybuty kategorii · ${karta.kategoria.nazwa}`}>
          <div className="grid gap-13 sm:grid-cols-2">
            {karta.atrybuty.map((definicja) => (
              <PoleAtrybutu
                key={definicja.kod}
                definicja={definicja}
                wartosc={wartosci[`atr:${definicja.kod}`] ?? ''}
                brakuje={brakujace.has(definicja.kod)}
                onZmiana={(wartosc) => ustaw(`atr:${definicja.kod}`, wartosc)}
              />
            ))}
          </div>
        </Karta>
      )}

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-13">
        <PrzyciskMarki type="submit" disabled={zapis}>
          {zapis ? 'Zapisywanie…' : 'Zapisz'}
        </PrzyciskMarki>
        <Przycisk type="button" onClick={onAnuluj} disabled={zapis}>
          Anuluj
        </Przycisk>
        <span className="t-small text-n-500">
          Puste pola są w porządku — kartę uzupełnia się przez lata.
        </span>
      </div>
    </form>
  );
}

function etykietaZBrakiem(etykieta: string, brakuje: boolean): string {
  // Dopisek mówi „tego jeszcze nie wiemy", nie „bez tego nie zapiszesz".
  return brakuje ? `${etykieta} · do uzupełnienia` : etykieta;
}

function przygotujAtrybut(definicja: DefinicjaAtrybutu, wartosc: string): unknown {
  if (definicja.typ === 'LICZBA') return Number(wartosc.replace(',', '.'));
  if (definicja.typ === 'BOOL') return wartosc === 'true';
  return wartosc;
}

function PoleAtrybutu({
  definicja,
  wartosc,
  brakuje,
  onZmiana,
}: {
  definicja: DefinicjaAtrybutu;
  wartosc: string;
  brakuje: boolean;
  onZmiana: (wartosc: string) => void;
}) {
  const etykieta = etykietaZBrakiem(
    definicja.jednostka ? `${definicja.nazwa} (${definicja.jednostka})` : definicja.nazwa,
    brakuje,
  );

  if (definicja.typ === 'WYBOR' || definicja.typ === 'BOOL') {
    const opcje =
      definicja.typ === 'BOOL'
        ? [
            { wartosc: 'true', nazwa: 'tak' },
            { wartosc: 'false', nazwa: 'nie' },
          ]
        : (definicja.walidacja?.opcje ?? []).map((opcja) => ({ wartosc: opcja, nazwa: opcja }));

    return (
      <label className="block">
        <span className="t-label mb-5 block text-n-700">{etykieta}</span>
        <select
          value={wartosc}
          onChange={(z) => onZmiana(z.target.value)}
          className="w-full border border-n-300 bg-surface px-13 text-n-900"
        >
          <option value="">—</option>
          {opcje.map((opcja) => (
            <option key={opcja.wartosc} value={opcja.wartosc}>
              {opcja.nazwa}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <PoleTekstowe
      etykieta={etykieta}
      type={definicja.typ === 'LICZBA' ? 'number' : definicja.typ === 'DATA' ? 'date' : 'text'}
      min={definicja.walidacja?.min}
      max={definicja.walidacja?.max}
      value={wartosc}
      onChange={(z) => onZmiana(z.target.value)}
    />
  );
}
